/**
 * EF-CLIENT-PORTAL-UI-V1
 * Client Crew Page
 * EF-DOCUMENT-ACCESS-READONLY-V1: Added document viewing capability
 * EF-DOCUMENT-PREVIEW-STANDARDISATION-V2: Integrated DocumentPreviewModal for consistent preview
 * 
 * Features:
 * - Crew cards with name, photo, role, project
 * - Data derived from shift assignments (backend handles this)
 * - Document viewing modal (read-only, verified docs only)
 * - Document preview with PDF/image support
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import {
  Users,
  Loader2,
  Briefcase,
  Calendar,
  User,
  FileText,
  Download,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
} from "lucide-react";
import { DocumentPreviewModal } from "../../components/shared/DocumentPreviewModal";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const CLIENT_AUTH_TOKEN_KEY = "eventsflo_client_auth_token";

export default function ClientCrewPage() {
  const navigate = useNavigate();
  const { getToken } = useOutletContext();
  // EVENTSFLO-PORTAL-I18N-PHASE-2B-CLIENT-PORTAL-COVERAGE-V1
  const { t } = useTranslation(["clientPortal", "common"]);

  const [loading, setLoading] = useState(true);
  const [crew, setCrew] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  
  // EF-DOCUMENT-ACCESS-READONLY-V1: Document viewing state
  // EF-DOCUMENT-PREVIEW-STANDARDISATION-V2: Added preview document state
  const [selectedCrewMember, setSelectedCrewMember] = useState(null);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetchCrew = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/client/crew`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch crew");

      const data = await response.json();
      setCrew(data.data || []);
      setMeta(data.meta || { page: 1, limit: 20, total: 0, total_pages: 0 });
    } catch (err) {
      console.error("Crew fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchCrew();
  }, [fetchCrew]);

  // EF-DOCUMENT-ACCESS-READONLY-V1: Fetch documents for crew member
  const fetchCrewDocuments = useCallback(async (crewId) => {
    const token = getToken();
    if (!token) return;

    setDocsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/client/crew/${crewId}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 403) {
          toast.error(t("clientPortal:crew.toast.docPermDenied"));
          return;
        }
        throw new Error("Failed to fetch documents");
      }

      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (err) {
      console.error("Documents fetch error:", err);
      toast.error(t("clientPortal:crew.toast.docsFetchFailed"));
    } finally {
      setDocsLoading(false);
    }
  }, [getToken, t]);

  // EF-DOCUMENT-ACCESS-READONLY-V1: Open documents modal
  const handleViewDocuments = (member) => {
    setSelectedCrewMember(member);
    setShowDocumentsModal(true);
    fetchCrewDocuments(member.id);
  };

  // EF-DOCUMENT-ACCESS-READONLY-V1: Download document
  // EF-DOCUMENT-PREVIEW-STANDARDISATION-V2: Updated to use consistent endpoint
  const handleDownloadDocument = async (docId, filename) => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/client/documents/${docId}/download?inline=false`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to download document");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "document";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t("clientPortal:crew.toast.docDownloaded"));
    } catch (err) {
      console.error("Download error:", err);
      toast.error(t("clientPortal:crew.toast.docDownloadFailed"));
    }
  };

  // EF-DOCUMENT-PREVIEW-STANDARDISATION-V2: Preview document handler
  const handlePreviewDocument = (doc) => {
    // Add the file_url for preview modal
    const docWithUrl = {
      ...doc,
      file_url: `/api/client/documents/${doc.id}/download`,
    };
    setPreviewDoc(docWithUrl);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase() || "?";
  };

  // Group assignments by project for display
  const getUniqueProjects = (assignments) => {
    const projectMap = new Map();
    assignments?.forEach((a) => {
      if (!projectMap.has(a.project_id)) {
        projectMap.set(a.project_id, {
          id: a.project_id,
          name: a.project_name,
          roles: new Set(),
        });
      }
      if (a.role) {
        projectMap.get(a.project_id).roles.add(a.role);
      }
    });
    return Array.from(projectMap.values());
  };

  // EF-DOCUMENT-ACCESS-READONLY-V1: Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // EF-DOCUMENT-ACCESS-READONLY-V1: Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold" data-testid="crew-title">{t("clientPortal:crew.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("clientPortal:crew.subtitle", { count: meta.total })}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : crew.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t("clientPortal:crew.empty")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {crew.map((member) => {
            const projects = getUniqueProjects(member.assignments);
            // Get all unique roles across assignments
            const allRoles = [...new Set(member.assignments?.map(a => a.role).filter(Boolean))];

            return (
              <Card
                key={member.id}
                className="hover:bg-accent/30 transition-colors"
                data-testid={`crew-card-${member.id}`}
              >
                <CardContent className="pt-5 pb-4">
                  {/* EF-CLIENT-PORTAL-UI-REFINEMENT-V1: Improved card layout */}
                  <div className="flex items-start gap-3">
                    {/* EVENTSFLO-PROFILE-PHOTO-SIGNED-URL-V1: Client
                        portal uses a different JWT (eventsflo_client_auth_token),
                        so the standard signed-URL mint endpoint isn't
                        available here. <AvatarImage> silently falls back
                        to AvatarFallback initials if the legacy URL 401s.
                        TODO: have /api/client/crew return pre-signed
                        photo URLs (follow-up prompt). */}
                    <Avatar className="h-11 w-11 shrink-0">
                      {member.profile_photo_url ? (
                        <AvatarImage src={member.profile_photo_url} alt={member.first_name} />
                      ) : null}
                      <AvatarFallback className="bg-primary/10 text-primary text-sm">
                        {getInitials(member.first_name, member.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm truncate">
                        {member.first_name} {member.last_name}
                      </h3>
                      {/* Roles - prominently displayed */}
                      {allRoles.length > 0 && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {allRoles.join(" • ")}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Assignments summary */}
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-muted-foreground">
                        {t("clientPortal:crew.projectsCount", { count: projects.length })}
                      </span>
                      {member.upcoming_shifts > 0 && (
                        <span className="text-primary font-medium">
                          {t("clientPortal:crew.upcomingCount", { count: member.upcoming_shifts })}
                        </span>
                      )}
                    </div>
                    
                    {/* Project list - compact */}
                    {projects.length > 0 && (
                      <div className="space-y-1">
                        {projects.slice(0, 2).map((project) => (
                          <div
                            key={project.id}
                            className="flex items-center gap-2 text-xs p-2 rounded bg-accent/40 cursor-pointer hover:bg-accent"
                            onClick={() => navigate(`/client/projects/${project.id}`)}
                          >
                            <Briefcase className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="truncate font-medium">{project.name}</span>
                          </div>
                        ))}
                        {projects.length > 2 && (
                          <p className="text-[10px] text-muted-foreground text-center pt-1">
                            {t("clientPortal:crew.moreProjects", { count: projects.length - 2 })}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* EF-DOCUMENT-ACCESS-READONLY-V1: View Documents Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-3 h-8 text-xs"
                      onClick={() => handleViewDocuments(member)}
                      data-testid={`view-docs-btn-${member.id}`}
                    >
                      <FileText className="h-3.5 w-3.5 mr-1.5" />
                      {t("clientPortal:crew.viewDocuments")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* EF-DOCUMENT-ACCESS-READONLY-V1: Documents Modal */}
      <Dialog open={showDocumentsModal} onOpenChange={setShowDocumentsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t("clientPortal:crew.docsModalTitle", {
                name: `${selectedCrewMember?.first_name || ""} ${selectedCrewMember?.last_name || ""}`.trim(),
              })}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            {docsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : documents.length === 0 ? (
              <div className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-50" />
                <p className="text-muted-foreground">{t("clientPortal:crew.noDocs")}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("clientPortal:crew.noDocsHint")}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("clientPortal:crew.table.document")}</TableHead>
                    <TableHead>{t("clientPortal:crew.table.type")}</TableHead>
                    <TableHead>{t("clientPortal:crew.table.expiry")}</TableHead>
                    <TableHead className="text-right">{t("clientPortal:crew.table.action")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium text-sm truncate max-w-[200px]">
                              {doc.original_filename}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(doc.file_size_bytes)}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {doc.document_type_name || doc.document_type_code || t("clientPortal:crew.typeOther")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {doc.expiry_date ? (
                          <div className="flex items-center gap-1">
                            {doc.is_expired ? (
                              <Badge className="bg-red-500/10 text-red-400 border-red-500/30 text-xs">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {t("clientPortal:crew.expired")}
                              </Badge>
                            ) : doc.is_expiring_soon ? (
                              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                                <Clock className="h-3 w-3 mr-1" />
                                {t("clientPortal:crew.expiringSoon")}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {formatDate(doc.expiry_date)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* EF-DOCUMENT-PREVIEW-STANDARDISATION-V2: Preview button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreviewDocument(doc)}
                            data-testid={`preview-doc-${doc.id}`}
                            title={t("clientPortal:crew.preview")}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                            data-testid={`download-doc-${doc.id}`}
                            title={t("clientPortal:crew.downloadDocument")}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* EF-DOCUMENT-PREVIEW-STANDARDISATION-V2: Document Preview Modal */}
      <DocumentPreviewModal
        document={previewDoc}
        open={!!previewDoc}
        onOpenChange={(open) => !open && setPreviewDoc(null)}
        fallbackFileUrl={previewDoc ? `${API_URL}/api/client/documents/${previewDoc?.id}/download` : undefined}
      />
    </div>
  );
}
